import { Suspense, useState } from "react";
import type { ContentFileDto } from "shared";
import { Icon } from "../../common/Icon";
import { Spinner } from "../../common/Spinner";
import { useSuspenseMediaItem } from "../../content/useMediaBatch";
import styles from "./MediaItem.module.css";

function getThumbnail(file: ContentFileDto) {
	if (Object.hasOwn(file.thumbnails, "256")) {
		return file.thumbnails["256"];
	}

	return file.url;
}

interface MediaItemProps {
	fileID: number;
}

export function MediaItem(props: MediaItemProps) {
	return (
		<div className={styles.container}>
			<Suspense fallback={<MediaItemLoading />}>
				<MediaItemContent {...props} />
			</Suspense>
		</div>
	);
}

function MediaItemLoading() {
	return (
		<div className={styles.overlay}>
			<Spinner />
		</div>
	);
}

function MediaItemError() {
	return (
		<div className={styles.overlay}>
			<Icon name="error" size={32} />
			There was an error loading media
		</div>
	);
}

function withVersion(url: string, cacheKey: string) {
	return `${url}?v=${encodeURIComponent(cacheKey)}`;
}

function MediaItemContent(props: MediaItemProps) {
	const file = useSuspenseMediaItem(props.fileID);
	const still = file.thumbnails["256"];
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	// HeyGen MP4s keep the moov atom at the end. A <video> with the default
	// metadata preload errors on that file, which covered the tile. The still
	// is the frame the media library already shows.
	const [useVideo, setUseVideo] = useState(file.type === "video" && !still);

	const handleLoad = () => {
		setLoading(false);
		setError(false);
	};

	const handleError = () => {
		setLoading(false);
		setError(true);
	};

	const handleStillError = () => {
		if (file.type === "video" && still) {
			setUseVideo(true);
			setLoading(true);
			setError(false);
			return;
		}
		handleError();
	};

	const cacheKey = file.importedAt ?? file.updatedAt;

	return (
		<>
			{loading && <MediaItemLoading />}
			{error && <MediaItemError />}
			{file.type === "video" && (
				<div className={styles.video}>
					<Icon name="videocam" size={18} />
				</div>
			)}
			{file.isUsed && <div className={styles.used}>Already used</div>}
			{file.type === "image" && (
				<img
					src={getThumbnail(file)}
					alt={file.filename}
					onLoad={handleLoad}
					onError={handleError}
				/>
			)}
			{file.type === "video" && !useVideo && still && (
				<img
					src={withVersion(still, cacheKey)}
					alt={file.filename}
					onLoad={handleLoad}
					onError={handleStillError}
				/>
			)}
			{file.type === "video" && useVideo && (
				<video
					tabIndex={-1}
					muted
					playsInline
					preload="auto"
					poster={still ? withVersion(still, cacheKey) : undefined}
					onError={handleError}
					onLoadedData={handleLoad}
				>
					<source src={withVersion(file.url, cacheKey)} type="video/mp4" />
				</video>
			)}
		</>
	);
}
